import React, { useState, useRef, useEffect } from 'react';
import '../styles/ColumnToggle.css';
import { Checkbox } from './index';
import { Columns } from '../assets/icons';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable item component with drag handle
function SortableItem({ column, onToggle }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: column.key,
        disabled: column.key === 'address' // Address column cannot be reordered
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : 'auto',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`column-toggle-item ${isDragging ? 'column-toggle-item-dragging' : ''}`}
        >
            <div
                className="column-toggle-drag-handle"
                {...attributes}
                {...listeners}
            >
                <span>⋮⋮</span>
            </div>
            <Checkbox
                label={column.label}
                selected={column.visible}
                type="default"
                disabled={column.key === 'address'}
                cta={() => onToggle(column.key)}
            />
        </div>
    );
}

function ColumnToggle({ columns, onToggle, onReorder }) {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Sensors for drag-and-drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        }),
        useSensor(KeyboardSensor)
    );

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const standardColumns = columns.filter(c => !c.isCustom);
    const customColumns = columns.filter(c => c.isCustom).sort((a, b) => a.label.localeCompare(b.label));

    // Handle drag end for reordering
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id && onReorder) {
            onReorder(active.id, over.id);
        }
    };

    return (
        <div className="column-toggle-container" ref={dropdownRef}>
            <button
                className="column-toggle-button"
                onClick={() => setOpen(!open)}
            >
                <Columns className="column-toggle-icon" />
                <span className="body-regular">Columns</span>
            </button>

            {open && (
                <div className="column-toggle-dropdown">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <div className="column-toggle-section">
                            <span className="label-bold-caps colour-quaternary">
                                Standard Columns
                            </span>
                            <SortableContext
                                items={standardColumns.map(c => c.key)}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="column-toggle-list">
                                    {standardColumns.map(col => (
                                        <SortableItem
                                            key={col.key}
                                            column={col}
                                            onToggle={onToggle}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </div>

                        {customColumns.length > 0 && (
                            <div className="column-toggle-section">
                                <span className="label-bold-caps colour-quaternary">
                                    Custom Fields
                                </span>
                                <SortableContext
                                    items={customColumns.map(c => c.key)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="column-toggle-list">
                                        {customColumns.map(col => (
                                            <SortableItem
                                                key={col.key}
                                                column={col}
                                                onToggle={onToggle}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                            </div>
                        )}
                    </DndContext>
                </div>
            )}
        </div>
    );
}

export default ColumnToggle;
